import type { CurrentSession } from '@elite-residence/api-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../api/ApiClientProvider';
import { useLocale } from '../i18n/LocaleProvider';
import { downloadFile } from '../ui/download';
import { Alert, Field, Loading, useAction } from '../ui/ui';

export function BookingDocuments({
  bookingId,
  session,
}: {
  readonly bookingId: string;
  readonly session: CurrentSession;
}) {
  const client = useApiClient();
  const queries = useQueryClient();
  const action = useAction();
  const ar = useLocale().locale === 'ar';
  const canView = session.permissions.includes('booking.view');
  const canUpload = session.permissions.includes('booking.edit');
  const documents = useQuery({
    queryKey: ['booking-documents', bookingId],
    enabled: canView,
    queryFn: async ({ signal }) => {
      const response = await client.listBookingDocuments({ path: { bookingId } }, { signal });
      if (response.status !== 200) throw new Error('booking_documents_unavailable');
      return response.body.documents;
    },
  });
  if (!canView && !canUpload) return null;
  return (
    <section className="er-panel er-section-block" aria-label={ar ? 'مستندات الحجز' : 'Booking documents'}>
      <div className="er-panel__head">
        <h2 className="er-panel__title">{ar ? 'مستندات الحجز' : 'Booking documents'}</h2>
        <span className="er-spacer" />
        <span className="er-cell-sub">{documents.data ? documents.data.length : ''}</span>
      </div>
      <div className="er-panel__body">
        <div className="er-upload">
          {action.error && <Alert kind="error">{action.error}</Alert>}
          {canUpload && (
            <Field id="booking-document-file" label={ar ? 'إرفاق مستند للحجز' : 'Attach booking document'}>
              <input
                id="booking-document-file"
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                disabled={action.busy}
                onChange={(event) => {
                  const input = event.target;
                  const file = input.files?.[0];
                  if (!file) return;
                  if (file.type !== 'application/pdf' && file.type !== 'image/jpeg' && file.type !== 'image/png') {
                    action.setError(ar ? 'اختر ملف PDF أو JPEG أو PNG.' : 'Choose a PDF, JPEG or PNG file.');
                    return;
                  }
                  const contentType = file.type;
                  void action.run(async (requestKey) => {
                    const response = await client.uploadBookingDocument({
                      path: { bookingId },
                      headers: { 'idempotency-key': requestKey },
                      body: new Uint8Array(await file.arrayBuffer()),
                      contentType,
                    });
                    if (response.status === 200 || response.status === 201) {
                      await queries.cancelQueries({ queryKey: ['booking-documents', bookingId] });
                      await queries.invalidateQueries({ queryKey: ['booking-documents', bookingId] });
                      input.value = '';
                    }
                    return response;
                  });
                }}
              />
            </Field>
          )}
          <span className="er-hint">
            {ar
              ? 'PDF أو JPEG أو PNG. العقد الموقّع وإثباتات العمليات تظهر في أقسامها.'
              : 'PDF, JPEG or PNG. The signed contract and transaction proofs stay in their own sections.'}
          </span>
        </div>
        {canView && documents.isPending && <Loading />}
        {canView && documents.isError && (
          <Alert kind="error">{ar ? 'تعذر تحميل مستندات الحجز.' : 'Booking documents could not be loaded.'}</Alert>
        )}
        {documents.data && documents.data.length === 0 && (
          <p>{ar ? 'لا توجد مستندات للحجز.' : 'No booking documents.'}</p>
        )}
        {documents.data && documents.data.length > 0 && (
          <table className="er-ledger-table">
            <tbody>
              {documents.data.map((document) => (
                <tr key={document.versionId}>
                  <td>
                    <span className="er-cell-main">{ar ? 'مستند الحجز' : 'Booking document'}</span>
                    <span className="er-cell-sub" style={{ display: 'block' }}>
                      v{document.versionNumber} · {document.contentType} · {document.byteSize} B
                    </span>
                  </td>
                  <td className="er-mono">{document.createdAt.slice(0, 10)}</td>
                  <td className="num">
                    <button
                      className="er-button"
                      type="button"
                      disabled={action.busy}
                      onClick={() => {
                        void action.run(async () => {
                          const response = await client.downloadBookingDocument({
                            path: { bookingId, versionId: document.versionId },
                          });
                          if (response.status === 200)
                            downloadFile(
                              response.body,
                              `booking-document-v${document.versionNumber}.${document.contentType === 'application/pdf' ? 'pdf' : document.contentType === 'image/png' ? 'png' : 'jpg'}`,
                            );
                          return response;
                        });
                      }}
                    >
                      {ar ? 'تنزيل المستند' : 'Download document'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
